import { Link } from 'react-router-dom';
import { featuredComics, comics } from '@/mocks/comics';
import { useState, useEffect, useCallback } from 'react';

export default function HeroSection() {
  const [currentIdx, setCurrentIdx] = useState(0);
  const featured = featuredComics.map((id) => comics.find((c) => c.id === id)!);

  const next = useCallback(() => setCurrentIdx((p) => (p + 1) % featured.length), [featured.length]);
  const prev = useCallback(() => setCurrentIdx((p) => (p - 1 + featured.length) % featured.length), [featured.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const comic = featured[currentIdx];

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={comic.cover}
          alt=""
          className="w-full h-full object-cover object-top scale-110 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background-50" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-14">
            {/* Cover */}
            <div className="w-44 h-60 md:w-52 md:h-72 rounded-lg overflow-hidden flex-shrink-0 ring-4 ring-background-50/20">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50">
                  HOT
                </span>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm">
                  {comic.status}
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                {comic.title}
              </h2>
              <p className="text-sm md:text-base text-background-50/70 line-clamp-2 max-w-lg mx-auto md:mx-0 mb-5">
                {comic.description}
              </p>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-6 flex-wrap">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-50/10 text-background-50/80 border border-background-50/10">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <Link
                  to={`/comic/${comic.id}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </Link>
                <Link
                  to={`/category/${comic.category}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-50/10 hover:bg-background-50/20 text-background-50 text-sm font-medium rounded-lg border border-background-50/10 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line"></i>
                  更多推荐
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {featured.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIdx(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIdx ? 'bg-primary-500 w-6' : 'bg-background-50/40 hover:bg-background-50/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
}